import type { Timestamp } from 'firebase/firestore'
import {
  AppBase,
  baseFromDoc,
  FirestoreBaseDoc,
  makeDocConverter,
} from '../base/firestore-model-base'
import { dateToJson } from '../../utils/date-util'
/** 1プロジェクトに1つのフォームとして使うときの固定ドキュメントID */
export const COMPANY_SURVEY_FORM_ID_CURRENT = 'current'

/** Firestoreに保存される形（Timestampのまま）。1プロジェクト1フォーム。送信先は company_survey_send_lists で別管理。 */
export type CompanySurveyFormDoc = FirestoreBaseDoc & {
  title: string
  description: string
  /** 任意質問1 */
  optional_question_1?: string | null
  /** 任意質問2 */
  optional_question_2?: string | null
  /** 任意質問3 */
  optional_question_3?: string | null
  /** 認証なしの town 画面で表示する学校情報（郵送確定時に保存） */
  school_name?: string | null
  school_principal?: string | null
  /** 学校の郵便番号（school_accounts.postal_code のスナップショット） */
  school_postal_code?: string | null
  school_address?: string | null
  school_phone?: string | null
  school_email?: string | null
  /** 認証なしの town 画面で表示するプロジェクト情報（郵送確定時に保存） */
  project_number?: string | null
  project_name?: string | null
  project_start_date?: string | null
  project_end_date?: string | null
  project_description?: string | null
  project_start_time?: string | null
  project_end_time?: string | null
  project_min_experience_days?: number | null
  project_min_experience_time?: number | null
  project_student_grade?: string | null
  participant_count?: number | null
  project_min_participants_per_company?: number | null
  project_max_participants_per_company?: number | null
  project_contact_person_name?: string | null
  project_lunch_notes?: string | null
  /** 事前連絡・訪問の実施予定（学校が入力。回答ページ表示用スナップショット） */
  project_prior_contact_visit_plan?: string | null
  /** 事前連絡・訪問の実施予定日（自由記述） */
  project_prior_contact_visit_schedule?: string | null
  /** 学校最寄り駅 */
  project_school_nearest_station?: string | null
  /** 希望する路線・駅と各駅からの所要時間 */
  project_preferred_route_and_travel_time?: string | null
  /** 回答期限（郵送確定時に設定） */
  deadline?: Timestamp | null
  /** 回答受付終了フラグ */
  closed?: boolean
  /** 学校が手動で回答受付を停止した際に入力した終了理由 */
  close_reason?: string | null
}

/** アプリで使う形（object / Date に変換）。送信先は CompanySurveySendList で別取得。 */
export type CompanySurveyForm = AppBase & {
  title: string
  description: string
  /** 任意質問1 */
  optionalQuestion1?: string
  /** 任意質問2 */
  optionalQuestion2?: string
  /** 任意質問3 */
  optionalQuestion3?: string
  /** 認証なしの town 画面で表示する学校情報 */
  schoolName?: string
  schoolPrincipal?: string
  schoolPostalCode?: string
  schoolAddress?: string
  schoolPhone?: string
  schoolEmail?: string
  /** 認証なしの town 画面で表示するプロジェクト情報 */
  projectNumber?: string
  projectName?: string
  projectStartDate?: string
  projectEndDate?: string
  projectDescription?: string
  projectStartTime?: string
  projectEndTime?: string
  projectMinExperienceDays?: number
  projectMinExperienceTime?: number
  projectStudentGrade?: string
  participantCount?: number
  projectMinParticipantsPerCompany?: number
  projectMaxParticipantsPerCompany?: number
  projectContactPersonName?: string
  projectLunchNotes?: string
  projectPriorContactVisitPlan?: string
  projectPriorContactVisitSchedule?: string
  projectSchoolNearestStation?: string
  projectPreferredRouteAndTravelTime?: string
  /** 回答期限 */
  deadline?: Date
  /** 回答受付終了フラグ */
  closed?: boolean
  /** 学校が手動で回答受付を停止した際に入力した終了理由 */
  closeReason?: string
}

export const companySurveyFormDocConverter = makeDocConverter<CompanySurveyFormDoc>()

export function toCompanySurveyForm(id: string, doc: CompanySurveyFormDoc, path: string): CompanySurveyForm {
  const rawDeadline = doc.deadline
  const deadline =
    rawDeadline != null && typeof (rawDeadline as Timestamp).toDate === 'function'
      ? (rawDeadline as Timestamp).toDate()
      : undefined

  return {
    ...baseFromDoc(id, doc, path),
    title: doc.title,
    description: doc.description ?? '',
    optionalQuestion1: doc.optional_question_1 ?? undefined,
    optionalQuestion2: doc.optional_question_2 ?? undefined,
    optionalQuestion3: doc.optional_question_3 ?? undefined,
    schoolName: doc.school_name ?? undefined,
    schoolPrincipal: doc.school_principal ?? undefined,
    schoolPostalCode: doc.school_postal_code ?? undefined,
    schoolAddress: doc.school_address ?? undefined,
    schoolPhone: doc.school_phone ?? undefined,
    schoolEmail: doc.school_email ?? undefined,
    projectNumber: doc.project_number ?? undefined,
    projectName: doc.project_name ?? undefined,
    projectStartDate: doc.project_start_date ?? undefined,
    projectEndDate: doc.project_end_date ?? undefined,
    projectDescription: doc.project_description ?? undefined,
    projectStartTime: doc.project_start_time ?? undefined,
    projectEndTime: doc.project_end_time ?? undefined,
    projectMinExperienceDays: doc.project_min_experience_days ?? undefined,
    projectMinExperienceTime: doc.project_min_experience_time ?? undefined,
    projectStudentGrade: doc.project_student_grade ?? undefined,
    participantCount: doc.participant_count ?? undefined,
    projectMinParticipantsPerCompany: doc.project_min_participants_per_company ?? undefined,
    projectMaxParticipantsPerCompany: doc.project_max_participants_per_company ?? undefined,
    projectContactPersonName: doc.project_contact_person_name ?? undefined,
    projectLunchNotes: doc.project_lunch_notes ?? undefined,
    projectPriorContactVisitPlan: doc.project_prior_contact_visit_plan ?? undefined,
    projectPriorContactVisitSchedule: doc.project_prior_contact_visit_schedule ?? undefined,
    projectSchoolNearestStation: doc.project_school_nearest_station ?? undefined,
    projectPreferredRouteAndTravelTime: doc.project_preferred_route_and_travel_time ?? undefined,
    deadline,
    closed: doc.closed ?? false,
    closeReason: doc.close_reason ?? undefined,
  }
}

function snapshotTimestampToJson(value: Timestamp | Date | undefined | null): string | null {
  if (value == null) return null
  if (value instanceof Date) return dateToJson(value)
  if (typeof (value as Timestamp).toDate === 'function') {
    return dateToJson((value as Timestamp).toDate())
  }
  return null
}

/** 履歴ドキュメント等へ保存するためのスナップショット（CompanySurveyFormDoc → JSON 用） */
export function toCompanySurveyFormSnapshotJson(
  doc: Partial<CompanySurveyFormDoc>,
  documentId: string = COMPANY_SURVEY_FORM_ID_CURRENT,
): Record<string, unknown> {
  return {
    title: doc.title ?? '',
    description: doc.description ?? '',
    optional_question_1: doc.optional_question_1 ?? null,
    optional_question_2: doc.optional_question_2 ?? null,
    optional_question_3: doc.optional_question_3 ?? null,
    school_name: doc.school_name ?? null,
    school_principal: doc.school_principal ?? null,
    school_postal_code: doc.school_postal_code ?? null,
    school_address: doc.school_address ?? null,
    school_phone: doc.school_phone ?? null,
    school_email: doc.school_email ?? null,
    project_number: doc.project_number ?? null,
    project_name: doc.project_name ?? null,
    project_start_date: doc.project_start_date ?? null,
    project_end_date: doc.project_end_date ?? null,
    project_description: doc.project_description ?? null,
    project_start_time: doc.project_start_time ?? null,
    project_end_time: doc.project_end_time ?? null,
    project_min_experience_days: doc.project_min_experience_days ?? null,
    project_min_experience_time: doc.project_min_experience_time ?? null,
    project_student_grade: doc.project_student_grade ?? null,
    participant_count: doc.participant_count ?? null,
    project_min_participants_per_company: doc.project_min_participants_per_company ?? null,
    project_max_participants_per_company: doc.project_max_participants_per_company ?? null,
    project_contact_person_name: doc.project_contact_person_name ?? null,
    project_lunch_notes: doc.project_lunch_notes ?? null,
    project_prior_contact_visit_plan: doc.project_prior_contact_visit_plan ?? null,
    project_prior_contact_visit_schedule: doc.project_prior_contact_visit_schedule ?? null,
    project_school_nearest_station: doc.project_school_nearest_station ?? null,
    project_preferred_route_and_travel_time: doc.project_preferred_route_and_travel_time ?? null,
    deadline: snapshotTimestampToJson(doc.deadline ?? null),
    closed: doc.closed ?? false,
    close_reason: doc.close_reason ?? null,
    created_at: snapshotTimestampToJson(doc.created_at),
    updated_at: snapshotTimestampToJson(doc.updated_at),
    is_deleted: doc.is_deleted ?? false,
    document_id: documentId,
  }
}

